import { stripDeallensIdPrefix } from "./deallensIdUtils";
import { normalizeDealLensStatus } from "./dealsConstants";
import { isVisibleOfferRow } from "./propertyOfferVisibility";
import {
  isOptimisticDealId,
  propertyOfferRowsMatch,
  resolveDealDeallensToken,
  resolvePropertyOfferListId,
  transformDealToOffer,
} from "./propertyOfferUtils";

/**
 * @param {object | null | undefined} deal
 * @returns {number}
 */
function dealInsertTimestamp(deal) {
  const time = new Date(deal?.insert_date ?? deal?.last_modified ?? "").getTime();
  return Number.isFinite(time) ? time : 0;
}

/**
 * Pick the newer collection tip when the API list and active deal disagree.
 *
 * @param {object} existingOffer
 * @param {object} deal
 * @returns {object}
 */
function resolveMergedCollectionDeal(existingOffer, deal) {
  const existingRaw = existingOffer?._raw ?? existingOffer;
  if (!existingRaw || !deal) return deal;

  const rowsMatch = propertyOfferRowsMatch(existingRaw, deal);
  const hubMirrorMatch = !rowsMatch && offerMatchesHubMirroredDeal(existingRaw, deal);
  if (!rowsMatch && !hubMirrorMatch) return deal;
  // Hub mirrors keep the share-row id — always prefer the real collection tip.
  if (hubMirrorMatch) return existingRaw;

  const existingToken = resolveDealDeallensToken(existingRaw);
  const dealToken = resolveDealDeallensToken(deal);
  const dealPrev = stripDeallensIdPrefix(deal?.metadata?.previous_deallens_id ?? "");
  const existingPrev = stripDeallensIdPrefix(existingRaw?.metadata?.previous_deallens_id ?? "");

  if (existingToken && dealPrev === existingToken) return deal;
  if (dealToken && existingPrev === dealToken) return existingRaw;

  return dealInsertTimestamp(deal) >= dealInsertTimestamp(existingRaw) ? deal : existingRaw;
}

/**
 * @param {object[]} propertyOffers
 * @param {object} deal
 * @returns {object}
 */
export function preferServerDealOverOptimisticDuplicate(propertyOffers, deal) {
  if (!deal || !isOptimisticDealId(deal)) return deal;

  const mutationParentToken = stripDeallensIdPrefix(deal.metadata?.previous_deallens_id ?? "");
  if (!mutationParentToken) return deal;

  const dealListId = resolvePropertyOfferListId(deal);
  const hasMatchingListRow = dealListId
    ? propertyOffers.some((offer) => String(offer?.id ?? "").trim() === dealListId)
    : false;

  for (const offer of propertyOffers) {
    const raw = offer?._raw ?? offer;
    if (!raw || isOptimisticDealId(raw)) continue;

    const incomingPrevToken = stripDeallensIdPrefix(raw.metadata?.previous_deallens_id ?? "");
    if (!incomingPrevToken || incomingPrevToken !== mutationParentToken) continue;

    if (hasMatchingListRow) {
      const rawListId = resolvePropertyOfferListId(raw);
      if (dealListId && rawListId && dealListId === rawListId) return raw;
      continue;
    }

    return raw;
  }

  return deal;
}

/**
 * Hub mirrors keep the share-row deallens id while the active tip lives under a
 * new collection_id. Treat "offer.previous === deal.id" as the same list row so
 * merge does not prepend a duplicate after the first collection-scoped offer.
 *
 * @param {object} offerRaw
 * @param {object} deal
 * @returns {boolean}
 */
function offerMatchesHubMirroredDeal(offerRaw, deal) {
  const dealToken = resolveDealDeallensToken(deal);
  if (!dealToken || isOptimisticDealId(deal)) return false;

  const offerPrevToken = stripDeallensIdPrefix(offerRaw?.metadata?.previous_deallens_id ?? "");
  if (!offerPrevToken || offerPrevToken !== dealToken) return false;

  const dealCollection = String(deal?.collection_id ?? "").trim();
  const offerCollection = String(offerRaw?.collection_id ?? "").trim();
  // After buildHubDisplayRow copies the tip collection, list ids already match.
  // This path covers older hub rows that still carry the share collection/id only.
  return !dealCollection || dealCollection !== offerCollection;
}

/**
 * @param {object} offer
 * @param {object} deal
 * @returns {boolean}
 */
function offerMatchesCurrentDeal(offer, deal) {
  const currentListId = resolvePropertyOfferListId(deal);
  const offerListId = String(offer?.id ?? "").trim();
  if (currentListId && offerListId && offerListId === currentListId) return true;

  const offerRaw = offer?._raw ?? offer;
  if (!offerRaw || !deal) return false;
  if (propertyOfferRowsMatch(offerRaw, deal)) return true;
  return offerMatchesHubMirroredDeal(offerRaw, deal);
}

/**
 * @param {object[]} offers
 * @param {object} deal
 * @param {number} keepIndex
 * @returns {object[]}
 */
function dedupeMatchingOffers(offers, deal, keepIndex) {
  return offers.filter(
    (offer, index) => index === keepIndex || !offerMatchesCurrentDeal(offer, deal),
  );
}

/**
 * Keep property-offer lists in sync with the active deal row (matched by collection_id).
 *
 * @param {object[]} propertyOffers
 * @param {object | null | undefined} deal
 * @param {object} [viewerContext]
 * @returns {object[]}
 */
export function mergeCurrentDealIntoPropertyOffers(propertyOffers, deal, viewerContext = {}) {
  const base = Array.isArray(propertyOffers) ? propertyOffers : [];
  if (!deal || !normalizeDealLensStatus(deal.status)) return base;

  const dealForMerge = preferServerDealOverOptimisticDuplicate(base, deal);
  const existingIndex = base.findIndex((offer) => offerMatchesCurrentDeal(offer, dealForMerge));
  const mergedDeal =
    existingIndex >= 0 ? resolveMergedCollectionDeal(base[existingIndex], dealForMerge) : dealForMerge;
  const dealToRender = preferServerDealOverOptimisticDuplicate(base, mergedDeal);
  const current = transformDealToOffer(dealToRender, 0, viewerContext);
  if (!isVisibleOfferRow(current)) return base;

  if (existingIndex >= 0) {
    const next = base.slice();
    next[existingIndex] = { ...current, number: base[existingIndex].number ?? existingIndex + 1 };
    return dedupeMatchingOffers(next, dealToRender, existingIndex);
  }

  const withoutMatches = base.filter((offer) => !offerMatchesCurrentDeal(offer, dealToRender));
  return [{ ...current, number: 1 }, ...withoutMatches.map((offer, index) => ({ ...offer, number: index + 2 }))];
}
